import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  doc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  useAuth,
  MENTOR_MESSAGE_CHARACTER_LIMIT,
} from "../../context/AuthContext";
import { useUniversities } from "../../hooks/useUniversities";
import {
  generateGraduationYears,
  industryCategories,
  OTHER_UNIVERSITY_ID,
  OTHER_UNIVERSITY_LABEL,
  OTHER_UNIVERSITY_NAME_MAX_LENGTH,
} from "../../data/mentorFormOptions";
import otpAuthService from "../../services/otpAuthService";
import Layout from "../layout/Layout";
import "./Auth.css";

const MentorRegister = () => {

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [universityId, setUniversityId] = useState("");
  const [otherUniversityName, setOtherUniversityName] = useState("");
  const [graduationYear, setGraduationYear] = useState("");
  const [industryCategory, setIndustryCategory] = useState("");
  const [industrySubcategory, setIndustrySubcategory] = useState("");
  const [industry, setIndustry] = useState(""); // Combined industry for Firestore compatibility
  const [company, setCompany] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [messageToStudents, setMessageToStudents] = useState("");

  // Form states
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [validInvitation, setValidInvitation] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [inviteCode, setInviteCode] = useState("");

  const { checkUserExists } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { universities, loading: universitiesLoading } = useUniversities();

  // Extract email and invitation code from URL on component mount
  useEffect(() => {
    const checkInvitation = async () => {
      try {
        // Parse URL params
        // Note: URLSearchParams treats '+' as space, so use regex+decodeURIComponent
        // to correctly handle email addresses that contain '+' (e.g. user+tag@example.com)
        const params = new URLSearchParams(location.search);
        const rawEmail = (location.search.match(/[?&]email=([^&]*)/) || [])[1];
        const emailParam = rawEmail ? decodeURIComponent(rawEmail) : null;
        const codeParam = params.get("code");
        setInviteCode(codeParam);

        if (!emailParam || !codeParam) {
          setError(
            "無効な招待リンクです。メールに記載されたリンクを正確にクリックしてください。"
          );
          setVerifying(false);
          setLoading(false);
          return;
        }

        setEmail(emailParam);

        // メールリンクによるサインインは行わない（フォーム送信時に新規登録を行う）

        // Verify the invitation code
        await verifyInvitation(emailParam, codeParam);
      } catch (error) {
        console.error("Invitation verification error:", error);
        setError("招待の確認中にエラーが発生しました。");
      } finally {
        setVerifying(false);
        setLoading(false);
      }
    };

    checkInvitation();
  }, [location]);

  // Verify invitation code against Firestore
  const verifyInvitation = async (email, code) => {
    try {
      const invitationsRef = collection(db, "invitations");
      const q = query(
        invitationsRef,
        where("email", "==", email),
        where("inviteCode", "==", code),
        where("status", "==", "pending")
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError(
          "この招待は無効か、既に使用されています。新しい招待を依頼してください。"
        );
        setValidInvitation(false);
        return;
      }

      // Check if invitation has expired
      const invitation = querySnapshot.docs[0].data();
      const invitationId = querySnapshot.docs[0].id;

      if (invitation.expiresAt && invitation.expiresAt.toDate() < new Date()) {
        setError(
          "招待の有効期限が切れています。新しい招待を依頼してください。"
        );
        setValidInvitation(false);

        // Best-effort: update invitation status (may fail if unauthenticated)
        try {
          await updateDoc(doc(db, "invitations", invitationId), {
            status: "expired",
          });
        } catch (updateError) {
          console.log("Could not update invitation status to expired:", updateError);
        }

        return;
      }

      // Valid invitation - store role if it exists
      setValidInvitation(true);
    } catch (error) {
      setError("招待の確認中にエラーが発生しました。");
      setValidInvitation(false);
    }
  };

  const handleUniversityChange = (e) => {
    const value = e.target.value;
    setUniversityId(value);
    if (value !== OTHER_UNIVERSITY_ID) {
      setOtherUniversityName("");
    }
  };

  // Handle industry category change
  const handleIndustryCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    setIndustryCategory(selectedCategory);
    setIndustrySubcategory(""); // Reset subcategory when category changes
    setIndustry(""); // Reset combined industry
  };

  // Handle industry subcategory change
  const handleIndustrySubcategoryChange = (e) => {
    const selectedSubcategory = e.target.value;
    setIndustrySubcategory(selectedSubcategory);
    
    // Combine category and subcategory for Firestore compatibility
    if (industryCategory && selectedSubcategory) {
      const combinedIndustry = selectedSubcategory === "その他" 
        ? industryCategory 
        : `${industryCategory} - ${selectedSubcategory}`;
      setIndustry(combinedIndustry);
    }
  };

  // Handle OTP verification
  const handleOTPVerification = async () => {
    if (otp.length !== 6) {
      setError("認証コードは6桁で入力してください。");
      return;
    }

    setOtpVerifying(true);
    setError("");

    try {
      const result = await otpAuthService.verifyMentorOTP(email, otp);
      if (result.success) {
        setEmailSent(true);
        setOtpSent(false);
        setSuccessMessage("メンター登録が完了しました！");

        // Redirect to mentor profile after successful registration
        setTimeout(() => {
          navigate("/mentor-profile");
        }, 2000);
      } else {
        setError("認証コードが正しくありません。入力内容をご確認のうえ、再度お試しください。");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      setError("認証コードの確認に失敗しました。入力内容をご確認のうえ、再度お試しください。");
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        setFieldErrors(prev => ({
          ...prev,
          profileImage: "画像サイズは5MB以下にしてください"
        }));
        e.target.value = null; // Clear the input
        return;
      }

      // Create image object to resize
      const img = new Image();
      const reader = new FileReader();
      
      reader.onloadend = (readerEvent) => {
        img.onload = () => {
          // Create canvas for resizing
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate new dimensions (max 800px width/height)
          const maxDimension = 800;
          let width = img.width;
          let height = img.height;
          
          if (width > height && width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw resized image
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob(() => {
            // Create preview from canvas
            const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
            setImagePreview(resizedDataUrl);
            setProfileImage(resizedDataUrl); // Store as base64 for now
            
            // Clear error if exists
            setFieldErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.profileImage;
              return newErrors;
            });
          }, 'image/jpeg', 0.9);
        };
        
        img.src = readerEvent.target.result;
      };
      
      reader.readAsDataURL(file);
    }
  };

  // Validate form before submission
  const validateForm = async () => {
    const errors = {};

    if (!name) errors.name = "氏名を入力してください";
    if (!universityId) {
      errors.universityId = "出身大学を選択してください";
    } else if (universityId === OTHER_UNIVERSITY_ID && !otherUniversityName.trim()) {
      errors.otherUniversityName = "出身大学名を入力してください";
    }

    if (!graduationYear) {
      errors.graduationYear = "卒業した年を選択してください";
    }

    if (!industry) {
      if (!industryCategory) {
        errors.industry = "就職先業界を選択してください";
      } else if (!industrySubcategory) {
        errors.industry = "就職先業界の詳細を選択してください";
      }
    }
    if (!company) errors.company = "会社名を入力してください";

    // 学生への一言メッセージを必須にする
    if (!messageToStudents || messageToStudents.trim() === "") {
      errors.messageToStudents = "学生への一言メッセージを入力してください";
    }

    // プロフィール画像を必須にする
    if (!profileImage) {
      errors.profileImage = "プロフィール画像を選択してください";
    }

    // メールアドレスの重複チェック（有効な招待がある場合はスキップ）
    if (email && !validInvitation) {
      const userExists = await checkUserExists(email);
      if (userExists) {
        errors.email = "このメールアドレスは既に登録されています。";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) return;

    setRegistering(true);
    setError("");

    try {
      const university = universities.find(u => u.id === universityId);
      const isOtherUniversity = universityId === OTHER_UNIVERSITY_ID;

      // メンター情報を準備
      const mentorData = {
        名前: name,
        氏名: name,
        出身大学: isOtherUniversity
          ? otherUniversityName.trim()
          : (university?.name || universityId),
        大学ID: universityId,
        isOfficialUniversity: !isOtherUniversity,
        卒業した年: graduationYear,
        就職先業界: industry,
        会社名: company,
        連絡先: email,
        アイコンURL: imagePreview || null,
        一言メッセージ: messageToStudents,
        urlParam: null,
      };

      // プロフィール画像がある場合はBase64として保存
      if (profileImage) {
        mentorData.profileImageFile = profileImage;
      }

      // メンター専用OTPを送信
      const result = await otpAuthService.sendMentorOTP(email, inviteCode, mentorData);

      if (result.success) {
        setOtpSent(true);
        setSuccessMessage(
          `${email} に認証コードを送信しました。メールを確認して入力してください。`
        );
      } else {
        throw new Error(result.message || "OTP送信に失敗しました");
      }
    } catch (error) {
      console.error("Registration error:", error);

      // Firebase Auth のエラーコードに応じて詳細を返す
      if (error.code === "auth/email-already-in-use") {
        setError(
          "このメールアドレスは既に使用されています。ログインしてください。"
        );
      } else if (error.code === "auth/invalid-email") {
        setError("メールアドレスの形式が正しくありません。");
      } else if (error.code === "auth/weak-password") {
        setError(
          "パスワードが弱すぎます。6文字以上のパスワードを使用してください。"
        );
      } else {
        setError("登録処理に失敗しました。時間をおいて再度お試しください。");
      }
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <Layout title="OG登録">
        <div className="login-box">
          <h2>招待の確認中...</h2>
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  // Show error if validation failed
  if (!validInvitation && !verifying) {
    return (
      <Layout title="無効な招待">
        <div className="login-box">
          <h2>招待の確認</h2>
          <div className="error-message">{error}</div>
          <div className="auth-links">
            <button onClick={() => navigate("/login")} className="link-button">
              ログインページへ
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="login-box">
        <h2>OG登録</h2>

        {error && <div className="error-message">{error}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}

        {otpSent ? (
          <div className="otp-verification">
            <p>認証コードを送信しました。</p>
            <p>{email} に送信された6桁のコードを入力してください。</p>
            <div className="form-group">
              <label htmlFor="otp">認証コード</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="6桁のコードを入力"
                maxLength="6"
                pattern="[0-9]{6}"
                required
              />
            </div>
            <button
              type="button"
              onClick={handleOTPVerification}
              className="auth-button"
              disabled={otpVerifying || otp.length !== 6}
            >
              {otpVerifying ? "確認中..." : "確認"}
            </button>
            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setOtp("");
              }}
              className="text-button"
            >
              戻る
            </button>
          </div>
        ) : !emailSent ? (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">メールアドレス</label>
              <input type="email" id="email" value={email} disabled readOnly />
              <small>このメールアドレスで招待されています</small>
              {fieldErrors.email && (
                <div className="error-message">{fieldErrors.email}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="name">
                氏名 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              {fieldErrors.name && (
                <div className="error-message">{fieldErrors.name}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="universityId">
                出身大学 <span className="required">*</span>
              </label>
              <select
                id="universityId"
                value={universityId}
                onChange={handleUniversityChange}
                required
                disabled={universitiesLoading}
              >
                <option value="">選択してください</option>
                {universities.map((uni) => (
                  <option key={uni.id} value={uni.id}>
                    {uni.name}
                  </option>
                ))}
                <option value={OTHER_UNIVERSITY_ID}>{OTHER_UNIVERSITY_LABEL}</option>
              </select>
              {universityId === OTHER_UNIVERSITY_ID && (
                <input
                  type="text"
                  id="otherUniversityName"
                  value={otherUniversityName}
                  onChange={(e) => setOtherUniversityName(e.target.value)}
                  placeholder="大学名を入力してください"
                  maxLength={OTHER_UNIVERSITY_NAME_MAX_LENGTH}
                  required
                  style={{ marginTop: "0.5rem" }}
                />
              )}
              {fieldErrors.universityId && (
                <div className="error-message">{fieldErrors.universityId}</div>
              )}
              {fieldErrors.otherUniversityName && (
                <div className="error-message">{fieldErrors.otherUniversityName}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="messageToStudents">
                学生への一言メッセージ <span className="required">*</span>
              </label>
              <textarea
                id="messageToStudents"
                value={messageToStudents}
                onChange={(e) => setMessageToStudents(e.target.value)}
                placeholder={`${MENTOR_MESSAGE_CHARACTER_LIMIT}文字以内で入力してください`}
                rows="4"
                maxLength={MENTOR_MESSAGE_CHARACTER_LIMIT}
                required
              />
              {fieldErrors.messageToStudents && (
                <div className="error-message">
                  {fieldErrors.messageToStudents}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="graduationYear">
                卒業した年 <span className="required">*</span>
              </label>
              <select
                id="graduationYear"
                value={graduationYear}
                onChange={(e) => setGraduationYear(e.target.value)}
                required
              >
                <option value="">選択してください</option>
                {generateGraduationYears().map((year) => (
                  <option key={year} value={year}>
                    {year}年
                  </option>
                ))}
              </select>
              {fieldErrors.graduationYear && (
                <div className="error-message">
                  {fieldErrors.graduationYear}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="industryCategory">
                就職先業界（大分類） <span className="required">*</span>
              </label>
              <select
                id="industryCategory"
                value={industryCategory}
                onChange={handleIndustryCategoryChange}
                required
              >
                <option value="">選択してください</option>
                {Object.keys(industryCategories).map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              {fieldErrors.industry && (
                <div className="error-message">{fieldErrors.industry}</div>
              )}
            </div>

            {industryCategory && (
              <div className="form-group">
                <label htmlFor="industrySubcategory">
                  就職先業界（詳細） <span className="required">*</span>
                </label>
                <select
                  id="industrySubcategory"
                  value={industrySubcategory}
                  onChange={handleIndustrySubcategoryChange}
                  required
                >
                  <option value="">選択してください</option>
                  {industryCategories[industryCategory]?.map((subcategory) => (
                    <option key={subcategory} value={subcategory}>
                      {subcategory}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {industryCategory === "その他" && (
              <div className="form-group" style={{display: 'none'}}>
                <small>「その他」が選択されました</small>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="company">
                会社名 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
              />
              {fieldErrors.company && (
                <div className="error-message">{fieldErrors.company}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="profileImage">
                プロフィール画像 <span className="required">*</span>
              </label>
              <input
                type="file"
                id="profileImage"
                accept="image/*"
                onChange={handleImageChange}
                required
              />
              <p className="field-hint">
                画像は自動的にリサイズされます（最大800px、5MB以下）
              </p>
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="プロフィール画像プレビュー" />
                </div>
              )}
              {fieldErrors.profileImage && (
                <div className="error-message">{fieldErrors.profileImage}</div>
              )}
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={registering}
            >
              {registering ? "送信中..." : "認証コードを送信"}
            </button>
          </form>
        ) : (
          <div className="email-sent-message">
            <p>登録が完了しました。</p>
            <button
              type="button"
              onClick={() => {
                navigate("/");
              }}
              className="text-button"
            >
              ホームに戻る
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MentorRegister;
